import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Col, Radio, Row, Select } from "antd";
import { GlobalTable } from "../../../global/components/GlobalTable";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { formatUTCTime } from "../../../global/utils/unixToLocalTime";

const initialFilterState = {
  Item: undefined,
  Code: undefined,
  Used: undefined,
};

const usedOptions = [
  { value: 1, label: "Yes" },
  { value: 0, label: "No" },
];

const getGiftCardColumns = (pagination, selectedRow, handleRadioChange) => [
  // {
  //   title: "No",
  //   dataIndex: "PurchaseNo",
  //   key: "PurchaseNo",
  // },
  {
    title: "Purchase Time",
    dataIndex: "PurchaseTime",
    key: "PurchaseTime",
    render: (text) => (text ? formatUTCTime(text) : ""),
  },
  {
    title: "License Item",
    dataIndex: "Item",
    key: "Item",
  },
  {
    title: "License",
    dataIndex: "CodeName",
    key: "CodeName",
  },
  {
    title: "Used",
    dataIndex: "Used",
    key: "Used",
  },
  {
    title: "Used MAC",
    dataIndex: "UsedMac",
    key: "UsedMac",
  },
  {
    title: "Used Time",
    dataIndex: "UsedTime",
    key: "UsedTime",
    render: (text) => (text ? formatUTCTime(text) : ""),
  },
  {
    title: "",
    key: "select",
    width: 50,
    align: "center",
    render: (_, record) =>
      record.Used === "Yes" ? null : (
        <Radio
          checked={selectedRow?.ItemNo === record.ItemNo}
          onChange={() => handleRadioChange(record)}
        />
      ),
  },
];

export const GiftCardItemsForm = forwardRef(({ dealerNo }, ref) => {
  const [listData, setListData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filter, setFilter] = useState(initialFilterState);
  const [params, setParams] = useState({
    limit: 10,
    page: 1,
    sort_by: "PurchaseTime",
    order: "desc",
  });

  const initialPagination = {
    current: 1,
    pageSize: 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);

  const queryObj = {
    ...params,
    ...(dealerNo != null && dealerNo !== "" ? { DealerNo: dealerNo } : {}),
  };
  const query = new URLSearchParams(queryObj).toString();
  const url =
    dealerNo != null && dealerNo !== ""
      ? `${process.env.REACT_APP_API_URL}/api/devices/gift-card-items/?${query}`
      : null;

  const { data, loading, refetch } = useGet(url);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const { data: optionData } = useGet(optionUrl);
  const itemOptions = getModifiedResponse(optionData, "items");

  const codeUrl = filter.Item
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=100&sort_by=Item&order=asc&page=1&Item=${filter.Item}`
    : null;
  const { data: codeData, loading: codeLoading } = useGet(codeUrl);
  const codeOptions = (codeData?.data ?? [])
    ?.filter((item) => !(filter.Item === 1 && [3, 4].includes(item.Code)))
    .map((item) => ({
      label: item.CodeName,
      value: item.Code,
    }));

  useImperativeHandle(ref, () => ({
    getSelectedRows: () => (selectedRow ? [selectedRow] : []),
  }));

  const handleRadioChange = (record) => {
    setSelectedRow(record);
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = (data?.data ?? []).map((el, index) => ({
        ...el,
        key: el?.ItemNo ?? el?.id ?? index,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize || prev.pageSize,
      }));
      setParams((prev) => ({
        ...prev,
        limit: pageSize || prev.limit,
        page,
      }));
    },
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";
    if (isEmpty) {
      return;
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
    setParams((prev) => ({
      ...prev,
      page: 1,
      ...filterDict,
    }));
    refetch();
  };

  const handleReset = () => {
    setFilter(initialFilterState);
    setPagination(initialPagination);
    setParams({
      limit: 10,
      page: 1,
      sort_by: "PurchaseTime",
      order: "desc",
    });
    refetch();
  };

  if (dealerNo == null || dealerNo === "") {
    return (
      <div className="py-4 text-gray-500">
        No dealer context. Please ensure you are logged in as a dealer.
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="mt-3">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              allowClear
              placeholder="License Item"
              options={itemOptions}
              value={filter.Item}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, Item: value, Code: undefined }))
              }
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              allowClear
              placeholder="License"
              options={codeOptions}
              value={filter.Code}
              loading={codeLoading}
              disabled={!filter.Item}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, Code: value }))
              }
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              allowClear
              placeholder="Used"
              options={usedOptions}
              value={filter.Used}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, Used: value }))
              }
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      </div>

      <div className="flex items-center my-4 flex-nowrap gap-2">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          onClick={handleFilter}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          className="reset-button w-[104px] h-[30px] font-medium"
          onClick={handleReset}
        >
          <HiArrowPath className="!text-[14px] font-medium" />
          Reset
        </Button>
      </div>

      <GlobalTable
        columns={getGiftCardColumns(pagination, selectedRow, handleRadioChange)}
        dataSource={listData}
        pagination={paginationConfig}
        loading={loading}
      />
    </div>
  );
});
